// Shared font catalog and lazy loader for the editor.
//
// `EDITOR_FONTS` is the canonical list consumed by every font dropdown
// (TextFloatingToolbar, RightSidebar typography, BrandKitPanel).
//
// `loadGoogleFont(name)` injects a <link rel="stylesheet"> at most once per
// family, so dropdowns can render each option in its own face without
// preloading the entire library up-front. UI-system fonts (Poppins, Inter,
// and OS fallbacks) are seeded into the loaded set so we don't redundantly
// fetch the running app shell font.

const SAFE_FONTS = new Set([
  'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana',
  'Courier New', 'Tahoma', 'Trebuchet MS', 'Impact', 'Comic Sans MS',
]);

// Families the app already provides — loadGoogleFont() must stay a no-op for
// these. Poppins is BUNDLED via @fontsource/poppins (src/index.css); Inter is
// loaded by the index.html <link>. Injecting a second Google stylesheet for
// either would define the family twice: the browser then renders with one
// @font-face group while the line-box strut can resolve against the other
// group's never-loaded face — `line-height: normal` computed from interim
// fallback metrics, text shifted lower (see PageThumbnailPreview).
const PRELOADED = new Set([
  'Poppins', 'Inter',
]);

const loaded = new Set([...SAFE_FONTS, ...PRELOADED]);
const inflight = new Map();

export const isWebSafeFont = (name) => SAFE_FONTS.has(name);

export const loadGoogleFont = (name) => {
  if (!name || loaded.has(name)) return Promise.resolve();
  if (inflight.has(name)) return inflight.get(name);

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(/%20/g, '+')}:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,700&display=swap`;

  const p = new Promise((resolve) => {
    link.onload = () => { loaded.add(name); resolve(); };
    link.onerror = () => { loaded.add(name); resolve(); };
    document.head.appendChild(link);
  });
  inflight.set(name, p);
  return p;
};

export const preloadFonts = (names) => {
  if (!Array.isArray(names)) return;
  names.forEach((n) => { loadGoogleFont(n); });
};

/**
 * Resolve only when the given font FACES are actually loaded (binaries in),
 * not merely when their stylesheets are.
 *
 * `loadGoogleFont` resolves on <link> load — that's the CSS, not the fonts;
 * face binaries are fetched lazily when layout first uses them. And
 * `document.fonts.ready` resolves against whatever is loading AT CALL TIME,
 * so it can fire before a lazily-triggered load is even registered. Both
 * together still leave a window where text lays out with fallback metrics.
 * `document.fonts.load(spec)` closes it: it explicitly starts the face load
 * and resolves only once the binary is available.
 *
 * @param {Array<{family:string, weight?:string, style?:string}>} specs
 */
export const waitForFontSpecs = async (specs) => {
  if (!Array.isArray(specs) || specs.length === 0) return;
  const families = Array.from(new Set(specs.map((s) => s && s.family).filter(Boolean)));
  await Promise.all(families.map((f) => loadGoogleFont(f)));
  if (typeof document === 'undefined' || !document.fonts) return;
  await Promise.all(specs.map(({ family, weight = '400', style = 'normal' }) =>
    document.fonts.load(`${style === 'italic' ? 'italic ' : ''}${weight} 16px "${family}"`).catch(() => {})
  ));
  // Also load EVERY registered face of each requested family/weight/style —
  // fonts.load() only fetches faces whose unicode-range covers the sample
  // text, leaving sibling subset faces (latin-ext, devanagari, …) pending.
  // WebKit resolves the `line-height: normal` strut against the family's
  // first declared face; when a cascade is re-realized (e.g. an ancestor
  // transform-scale change on hover) and that face is still pending, the
  // strut silently takes interim/LastResort metrics (~1.42em ascent) and the
  // whole line drops — permanently. Loading all faces removes the "pending
  // face" state entirely, so the strut is stable in every engine.
  try {
    const wanted = new Set(specs.map(({ family, weight = '400', style = 'normal' }) => `${family}|${weight}|${style}`));
    const loads = [];
    document.fonts.forEach((face) => {
      const fam = String(face.family || '').replace(/^['"]|['"]$/g, '');
      const w = face.weight === 'normal' ? '400' : face.weight === 'bold' ? '700' : String(face.weight);
      const st = face.style === 'italic' ? 'italic' : 'normal';
      if (wanted.has(`${fam}|${w}|${st}`) && face.status === 'unloaded') {
        loads.push(face.load().catch(() => {}));
      }
    });
    await Promise.all(loads);
  } catch { /* best effort */ }
  await document.fonts.ready;
};

// ─── Catalog ────────────────────────────────────────────────────────────────
// Curated set of widely-used Google Fonts covering the categories a designer
// editor needs. Kept under ~70 to balance UX and memory: each rendered option
// loads its own face on first visibility.

export const FONT_CATEGORIES = [
  {
    id: 'sans',
    label: 'Sans Serif',
    fonts: [
      'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
      'Raleway', 'Nunito', 'Ubuntu', 'Source Sans 3', 'Noto Sans',
      'DM Sans', 'Outfit', 'Plus Jakarta Sans', 'Work Sans', 'Manrope',
      'Figtree', 'Karla', 'Quicksand', 'Rubik', 'Barlow', 'IBM Plex Sans',
      'Archivo', 'Public Sans', 'Mulish', 'Be Vietnam Pro', 'Heebo', 'Cabin',
      'Hind', 'Mukta', 'PT Sans', 'Noto Sans Display', 'Nunito Sans',
      'Roboto Condensed', 'Roboto Flex', 'Titillium Web', 'Asap',
      'Catamaran', 'Exo 2', 'Encode Sans', 'Maven Pro', 'Overpass',
      'Prompt', 'Kanit', 'Saira', 'Signika', 'Yantramanav', 'Jost',
      'Onest', 'Sora', 'Spline Sans', 'Hanken Grotesk', 'Bricolage Grotesque',
      'Fira Sans', 'Assistant', 'Lexend', 'Lexend Deca', 'Red Hat Display',
      'Red Hat Text', 'Cairo', 'Tajawal', 'Inconsolata', 'Commissioner',
      'Geist', 'Onest',
    ],
  },
  {
    id: 'serif',
    label: 'Serif',
    fonts: [
      'Playfair Display', 'Merriweather', 'Lora', 'PT Serif',
      'Cormorant Garamond', 'EB Garamond', 'Crimson Text', 'Crimson Pro',
      'Libre Baskerville', 'Bitter', 'Roboto Slab', 'Slabo 27px',
      'Vollkorn', 'Source Serif 4', 'Noto Serif', 'Cardo',
      'Spectral', 'Frank Ruhl Libre', 'Bree Serif', 'Arvo',
      'Old Standard TT', 'Cormorant', 'Cormorant Infant',
      'Libre Caslon Text', 'Newsreader', 'Literata', 'IBM Plex Serif',
      'Source Serif Pro', 'Tinos', 'Domine', 'Gentium Book Plus',
      'Yeseva One', 'Marcellus', 'Petrona', 'DM Serif Display',
      'DM Serif Text', 'Inria Serif',
    ],
  },
  {
    id: 'display',
    label: 'Display',
    fonts: [
      'Oswald', 'Bebas Neue', 'Anton', 'Russo One', 'Righteous',
      'Black Ops One', 'Bungee', 'Abril Fatface', 'Ultra', 'Alfa Slab One',
      'Fjalla One', 'Staatliches', 'Archivo Black', 'Bowlby One',
      'Bungee Shade', 'Carter One', 'Faster One', 'Fugaz One',
      'Squada One', 'Bangers', 'Monoton', 'Pirata One',
      'Rubik Mono One', 'Bowlby One SC', 'Passion One', 'Limelight',
      'Bungee Inline', 'Bowlby One', 'Big Shoulders Display',
      'Audiowide', 'Wallpoet', 'Major Mono Display',
    ],
  },
  {
    id: 'script',
    label: 'Script',
    fonts: [
      'Dancing Script', 'Pacifico', 'Lobster', 'Sacramento',
      'Great Vibes', 'Satisfy', 'Kaushan Script', 'Allura',
      'Pinyon Script', 'Tangerine', 'Parisienne', 'Yellowtail',
      'Marck Script', 'Cookie', 'Alex Brush', 'Pacifico',
      'Petit Formal Script', 'Mrs Saint Delafield', 'Mr Dafoe',
      'Italianno', 'Berkshire Swash', 'Niconne', 'Sofia',
      'Damion',
    ],
  },
  {
    id: 'handwriting',
    label: 'Handwriting',
    fonts: [
      'Caveat', 'Indie Flower', 'Shadows Into Light', 'Permanent Marker',
      'Architects Daughter', 'Patrick Hand', 'Gloria Hallelujah',
      'Reenie Beanie', 'Just Another Hand', 'Kalam', 'Amatic SC',
      'Homemade Apple', 'Rock Salt', 'Mali', 'Annie Use Your Telescope',
      'Coming Soon', 'Caveat Brush', 'Schoolbell', 'Walter Turncoat',
      'La Belle Aurore', 'Nothing You Could Do', 'Neucha', 'Sue Ellen Francisco',
    ],
  },
  {
    id: 'mono',
    label: 'Monospace',
    fonts: [
      'Roboto Mono', 'Source Code Pro', 'JetBrains Mono', 'Fira Code',
      'IBM Plex Mono', 'Space Mono', 'Inconsolata', 'Anonymous Pro',
      'Cousine', 'Cutive Mono', 'Nova Mono', 'Overpass Mono',
      'Share Tech Mono', 'Ubuntu Mono', 'VT323', 'Major Mono Display',
    ],
  },
  {
    id: 'system',
    label: 'System',
    fonts: ['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana',
      'Courier New', 'Tahoma', 'Trebuchet MS', 'Impact', 'Comic Sans MS'],
  },
];

export const EDITOR_FONTS = Array.from(new Set(FONT_CATEGORIES.flatMap((c) => c.fonts)));

export const findFontCategory = (name) => {
  for (const c of FONT_CATEGORIES) {
    if (c.fonts.includes(name)) return c.id;
  }
  return null;
};
